import * as React from 'react';
import moment from 'moment';
import gql from 'graphql-tag';
import AuthorizedQuery from './AuthorizedQuery';

const GQL = gql(`
  query Tracking($fieldId: ID!, $filter: FieldDocumentFilter) {
    field(id: $fieldId) {
      id
      name
      library(filter: $filter, sort: capturedOnAsc, first: 100) {
        nodes {
          id
          capturedOn
          thumbnailUrl
          ... on RasterDocument {
            meanNdvi
          }
        }
      }
    }
  }
`);

export interface DataPoint {
  id: string;
  date: number;
  meanNdvi: number;
  thumbnailUrl: string;
}

interface Renders {
  points?: DataPoint[] | null;
  loading: boolean;
}

type ChildrenFn = (renders: Renders) => React.ReactNode;

interface Props {
  fieldId: string;
  capturedSince: string;
  capturedUntil: string;
  children: ChildrenFn;
}

const mapPropsToVariables = (props: Props) => ({
  fieldId: props.fieldId,
  filter: {
    kind: 'raster-ndvi',
    sources: ['sentinel', 'sentinel2'],
    capturedSince: moment(props.capturedSince).format('x'),
    capturedUntil: moment(props.capturedUntil).format('x')
  }
});

const mapDocumentToDataPoint = (doc: RasterDocument): DataPoint | null => {
  if (!doc.meanNdvi) {
    return null;
  }

  return {
    id: doc.id,
    date: parseInt(doc.capturedOn, 10),
    meanNdvi: doc.meanNdvi,
    thumbnailUrl: doc.thumbnailUrl
  };
};

const renderChildren = (
  props: Props,
  data: any,
  loading: boolean
): React.ReactNode => {
  if (loading) {
    return props.children({ loading: true });
  }

  const field = data && data.field;
  const library = field && field.library;
  const docs = (library && library.nodes) || null;
  const points = docs && docs.filter((d: any) => !!d.meanNdvi).map(mapDocumentToDataPoint);

  return props.children({ points, loading: false });
};

const TrackingDataProvider: React.StatelessComponent<Props> = (
  props: Props
) => (
  <AuthorizedQuery
    query={GQL}
    variables={mapPropsToVariables(props)}
    children={({ data, loading }) => renderChildren(props, data, loading)}
  />
);

export default TrackingDataProvider;



// WEBPACK FOOTER //
// ./src/providers/TrackingDataProvider.tsx